import React from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { WaitingUserItem } from "../types";
import { ListItemAction } from "@components/ListItemCardCst/types";
import MoreActionsMenu from "@components/ListItemCardCst/MoreActionsMenu";
import BadgeBoxCst from "@components/BadgeBoxCst";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

interface WaitingUserRowProps {
  user: WaitingUserItem;
  approved: boolean;
  onApproveToggle: (next: boolean) => void;
  onArrowClick?: (user: WaitingUserItem) => void;
  moreActions?: ListItemAction[] | ListItemAction[][];
  disabled?: boolean;
}

const WaitingUserRow: React.FC<WaitingUserRowProps> = ({
  user,
  approved,
  onApproveToggle,
  onArrowClick,
  moreActions,
  disabled,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const fullName =
    [user.userName, user.userSurname].filter(Boolean).join(" ").trim() || "-";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "14px 16px",
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down("lg")]: {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.5,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {fullName}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: theme.palette.text.primary,
            lineHeight: 1.4,
          }}
        >
          {`ID: ${user.userId ?? "-"}${
            user.userEmail ? ` | ${user.userEmail}` : ""
          }`}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <ButtonSecondary
            size="sm"
            disabled={disabled}
            onClick={() => onApproveToggle(false)}
          >
            {t("row.deny-auto-enrollment")}
          </ButtonSecondary>
          <ButtonPrimary
            size="sm"
            disabled={disabled}
            onClick={() => onApproveToggle(true)}
          >
            {t("row.approve-auto-enrollment")}
          </ButtonPrimary>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "space-between",
          alignSelf: "stretch",
          gap: 1,
          flexShrink: 0,
          [theme.breakpoints.down("lg")]: {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BadgeBoxCst>{t("row.approval-required-badge")}</BadgeBoxCst>

          {onArrowClick && (
            <IconButton
              size="small"
              onClick={() => onArrowClick(user)}
              sx={{ color: theme.palette.primary.main }}
              aria-label="open detail"
            >
              <ArrowRight size={18} />
            </IconButton>
          )}
        </Box>

        <MoreActionsMenu actions={moreActions} disabled={disabled} />
      </Box>
    </Box>
  );
};

export default WaitingUserRow;
